/**
 * SMS admin hooks — templates / messages / manual send / account info.
 *
 * Direct fetch with credentials (nested route paths don't trace cleanly
 * through the Hono RPC client).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { env } from './env';

export const SMS_TEMPLATES_KEY = ['admin', 'sms', 'templates'] as const;
export const SMS_MESSAGES_KEY = ['admin', 'sms', 'messages'] as const;
export const SMS_ACCOUNT_KEY = ['admin', 'sms', 'account'] as const;

export type TemplateKind =
  | 'booking_confirmation'
  | 'booking_reminder_t24h'
  | 'booking_reminder_t2h'
  | 'booking_cancellation'
  | 'recall'
  | 'custom';

export interface SmsTemplate {
  kind: TemplateKind;
  name: string;
  body: string;
  enabled: boolean;
  is_marketing: boolean;
  variables: string[];
  customized: boolean;
}

export interface SmsMessage {
  id: string;
  to_phone_redacted: string | null;
  body: string;
  parts: number;
  cost_points: number | null;
  status: 'pending' | 'queued' | 'delivered' | 'failed' | 'blocked';
  status_detail: string | null;
  template_kind: string | null;
  context_kind: string | null;
  context_id: string | null;
  delivered_at: number | null;
  created_at: number;
}

export interface SmsAccount {
  configured: boolean;
  sender_id?: string;
  username?: string;
  points_remaining?: string;
  error?: string;
}

async function adminFetch(path: string, init: RequestInit = {}) {
  return fetch(`${env.apiBaseUrl}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      accept: 'application/json',
      'content-type': 'application/json',
      ...(init.headers ?? {}),
    },
  });
}

export function useSmsAccount() {
  return useQuery({
    queryKey: SMS_ACCOUNT_KEY,
    queryFn: async (): Promise<SmsAccount> => {
      const res = await adminFetch('/api/admin/sms/account');
      if (!res.ok) throw new Error('account_failed');
      return (await res.json()) as SmsAccount;
    },
    staleTime: 60_000,
  });
}

export function useSmsTemplates() {
  return useQuery({
    queryKey: SMS_TEMPLATES_KEY,
    queryFn: async (): Promise<SmsTemplate[]> => {
      const res = await adminFetch('/api/admin/sms/templates');
      if (!res.ok) throw new Error('templates_failed');
      const json = (await res.json()) as { templates: SmsTemplate[] };
      return json.templates;
    },
  });
}

export function useSaveSmsTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { kind: TemplateKind; name: string; body: string; enabled: boolean }) => {
      const { kind, ...rest } = input;
      const res = await adminFetch(`/api/admin/sms/templates/${kind}`, {
        method: 'PUT',
        body: JSON.stringify(rest),
      });
      if (!res.ok) throw new Error('save_failed');
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: SMS_TEMPLATES_KEY }),
  });
}

export function useResetSmsTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (kind: TemplateKind) => {
      const res = await adminFetch(`/api/admin/sms/templates/${kind}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('reset_failed');
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: SMS_TEMPLATES_KEY }),
  });
}

export function useSmsMessages() {
  return useQuery({
    queryKey: SMS_MESSAGES_KEY,
    queryFn: async (): Promise<SmsMessage[]> => {
      const res = await adminFetch('/api/admin/sms/messages');
      if (!res.ok) throw new Error('messages_failed');
      const json = (await res.json()) as { messages: SmsMessage[] };
      return json.messages;
    },
  });
}

export function useSendSms() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { to: string; message: string; test?: boolean }) => {
      const res = await adminFetch('/api/admin/sms/send', {
        method: 'POST',
        body: JSON.stringify(input),
      });
      if (!res.ok) throw new Error('send_failed');
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: SMS_MESSAGES_KEY }),
  });
}

/** Char + segment counter mirroring server-side estimateSegments(). */
export function estimateSegments(message: string): { chars: number; segments: number; encoding: 'gsm-7' | 'ucs-2' } {
  const hasUnicode = /[^\x00-\x7F€£§¶àéùìòÄ-ÿ]/u.test(message);
  const chars = message.length;
  if (hasUnicode) {
    const seg = chars <= 70 ? 70 : 67;
    return { chars, segments: Math.ceil(chars / seg), encoding: 'ucs-2' };
  }
  const seg = chars <= 160 ? 160 : 153;
  return { chars, segments: Math.ceil(chars / seg), encoding: 'gsm-7' };
}
